import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatDate, getErrorMessage } from '@/lib/utils';
import { agencyAPI, auditAPI, communicationGovernanceAPI } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Inbox, ShieldCheck, Clock, Check, X,
} from 'lucide-react';

const POLICY_LABEL = {
  direct_contact_allowed: 'Direct Contact Allowed',
  agency_coordination_required: 'Agency Coordination Required',
  restricted_direct_contact: 'Restricted Direct Contact',
};
const POLICY_TONE = {
  direct_contact_allowed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  agency_coordination_required: 'bg-amber-100 text-amber-700 border-amber-200',
  restricted_direct_contact: 'bg-rose-100 text-rose-700 border-rose-200',
};

const AUDIT_ACTIONS = [
  'communication_policy.agency_updated',
  'communication_policy.change_requested',
  'communication_policy.request_approved',
  'communication_policy.request_rejected',
  'communication_policy.agent_exception_created',
  'communication_policy.agent_exception_updated',
  'communication_policy.agent_exception_removed',
  'communication_policy.agent_exception_requested',
  'communication_policy.agent_exception_approved',
  'communication_policy.agent_exception_rejected',
];

const PolicyBadge = ({ value }) => (
  <Badge
    className={`text-xs border ${POLICY_TONE[value] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
  >
    {POLICY_LABEL[value] || value || '—'}
  </Badge>
);

export default function CommunicationGovernancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isKR = isKRRole(user);

  const [pending, setPending] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Route-level guard: non-KR is bounced to the dashboard rather than
  // seeing an unauthorized shell.
  useEffect(() => {
    if (user && !isKR) navigate('/dashboard', { replace: true });
  }, [user, isKR, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, agencyRes, auditRes, agentAuditRes] = await Promise.all([
        communicationGovernanceAPI.listRequests(),
        agencyAPI.list(),
        auditAPI.getLogs({ page_size: 50, entity_type: 'agency' }),
        auditAPI.getLogs({ page_size: 50, entity_type: 'agent' }),
      ]);
      setPending(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setAgencies(Array.isArray(agencyRes.data) ? agencyRes.data : []);
      const rawAgency = auditRes.data?.items || auditRes.data?.logs || auditRes.data || [];
      const rawAgent = agentAuditRes.data?.items || agentAuditRes.data?.logs || agentAuditRes.data || [];
      const combined = [...(Array.isArray(rawAgency) ? rawAgency : []), ...(Array.isArray(rawAgent) ? rawAgent : [])];
      const filtered = combined.filter((row) => AUDIT_ACTIONS.includes(row?.action));
      filtered.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setAuditRows(filtered.slice(0, 30));
    } catch (e) {
      console.error('Failed to load communication governance data:', e);
      toast.error(getErrorMessage(e, 'Failed to load governance data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isKR) load();
  }, [isKR, load]);

  const activePolicies = useMemo(() => {
    // Enrich each agency with its resolved-at-agency-level policy state.
    return agencies.map((a) => ({
      agency_id: a.agency_id,
      agency_name: a.agency_name,
      policy: a?.communication_policy?.value || 'direct_contact_allowed',
      source: a?.communication_policy?.value ? 'agency' : 'platform',
      updated_by: a?.communication_policy?.set_by_user_email,
      updated_at: a?.communication_policy?.set_at,
    }));
  }, [agencies]);

  const agencyRequests = useMemo(
    () => pending.filter((r) => r.type !== 'agent_communication_exception'),
    [pending],
  );
  const agentRequests = useMemo(
    () => pending.filter((r) => r.type === 'agent_communication_exception'),
    [pending],
  );

  const handleApprove = async (row) => {
    setSubmitting(true);
    try {
      if (row.type === 'agent_communication_exception') {
        await communicationGovernanceAPI.approveAgentException(row.npn);
      } else {
        await communicationGovernanceAPI.approve(row.agency_id);
      }
      toast.success('Request approved');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to approve request'));
    } finally {
      setSubmitting(false);
    }
  };

  const openReject = (row) => {
    setRejectTarget(row);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.error('A reason is required to reject a request');
      return;
    }
    setSubmitting(true);
    try {
      if (rejectTarget.type === 'agent_communication_exception') {
        await communicationGovernanceAPI.rejectAgentException(rejectTarget.npn, rejectReason.trim());
      } else {
        await communicationGovernanceAPI.reject(rejectTarget.agency_id, rejectReason.trim());
      }
      toast.success('Request rejected');
      setRejectTarget(null);
      setRejectReason('');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to reject request'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isKR) return null;

  return (
    <div data-testid="communication-governance-page">
      <PageHeader
        title="Communication Governance"
        description="Review and manage agency communication policies and agent exceptions."
        breadcrumbs={[{ label: 'Governance', href: '/governance/communication-policy' }]}
      />
      <div className="page-content space-y-8">
        {/* Agency Policy Requests */}
        <section data-testid="governance-agency-requests-section">
          <div className="mb-3 flex items-center gap-2">
            <Inbox size={16} className="text-sky-600" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Agency Policy Requests
            </h2>
            <Badge className="bg-sky-100 text-sky-700 text-xs">{agencyRequests.length}</Badge>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-400" data-testid="governance-agency-loading">
                  Loading agency requests…
                </div>
              ) : agencyRequests.length === 0 ? (
                <div className="p-6 text-center" data-testid="governance-agency-empty">
                  <p className="text-sm font-medium text-slate-800">No pending agency policy requests</p>
                  <p className="text-xs text-slate-500 mt-1">
                    New change requests from agency principals will appear here.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agency</TableHead>
                      <TableHead>Current Policy</TableHead>
                      <TableHead>Requested Policy</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Requested Date</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agencyRequests.map((row) => (
                      <TableRow key={`agency-${row.agency_id}`} data-testid={`governance-request-row-${row.agency_id}`}>
                        <TableCell className="font-medium">{row.agency_name}</TableCell>
                        <TableCell><PolicyBadge value={row.current_policy} /></TableCell>
                        <TableCell><PolicyBadge value={row.requested_policy} /></TableCell>
                        <TableCell className="text-sm text-slate-600">{row.requested_by}</TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDate(row.requested_at)}</TableCell>
                        <TableCell className="max-w-xs text-sm text-slate-600 truncate" title={row.reason}>{row.reason}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(row)}
                            disabled={submitting}
                            data-testid={`governance-approve-btn-${row.agency_id}`}
                          >
                            <Check size={14} className="mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReject(row)}
                            disabled={submitting}
                            data-testid={`governance-reject-btn-${row.agency_id}`}
                          >
                            <X size={14} className="mr-1" /> Reject
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Agent Exception Requests */}
        <section data-testid="governance-agent-requests-section">
          <div className="mb-3 flex items-center gap-2">
            <Inbox size={16} className="text-indigo-600" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Agent Exception Requests
            </h2>
            <Badge className="bg-indigo-100 text-indigo-700 text-xs">{agentRequests.length}</Badge>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-400" data-testid="governance-agent-loading">
                  Loading agent exception requests…
                </div>
              ) : agentRequests.length === 0 ? (
                <div className="p-6 text-center" data-testid="governance-agent-empty">
                  <p className="text-sm font-medium text-slate-800">No pending agent exception requests</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Agent or principal-submitted exception requests will appear here.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Agency</TableHead>
                      <TableHead>Current Policy</TableHead>
                      <TableHead>Requested Policy</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Requested Date</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agentRequests.map((row) => (
                      <TableRow key={`agent-${row.npn}`} data-testid={`governance-agent-request-row-${row.npn}`}>
                        <TableCell className="font-medium">
                          <div>{row.agent_name}</div>
                          <div className="text-xs text-slate-500">NPN {row.npn}</div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{row.agency_name || '—'}</TableCell>
                        <TableCell><PolicyBadge value={row.current_policy} /></TableCell>
                        <TableCell><PolicyBadge value={row.requested_policy} /></TableCell>
                        <TableCell className="text-sm text-slate-600">
                          <div>{row.requested_by}</div>
                          {row.requested_by_role && (
                            <div className="text-xs text-slate-400">
                              {row.requested_by_role === 'agent' ? 'Agent' : 'Agency Principal'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDate(row.requested_at)}</TableCell>
                        <TableCell className="max-w-xs text-sm text-slate-600 truncate" title={row.reason}>{row.reason}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {row.expires_at ? formatDate(row.expires_at) : '—'}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(row)}
                            disabled={submitting}
                            data-testid={`governance-agent-approve-btn-${row.npn}`}
                          >
                            <Check size={14} className="mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReject(row)}
                            disabled={submitting}
                            data-testid={`governance-agent-reject-btn-${row.npn}`}
                          >
                            <X size={14} className="mr-1" /> Reject
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Active Policies */}
        <section data-testid="governance-active-policies">
          <div className="mb-3 flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Active Policies
            </h2>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-400">Loading agencies…</div>
              ) : activePolicies.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">No agencies on file.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agency</TableHead>
                      <TableHead>Current Policy</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Updated By</TableHead>
                      <TableHead>Last Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activePolicies.map((row) => (
                      <TableRow key={row.agency_id} data-testid={`governance-active-row-${row.agency_id}`}>
                        <TableCell className="font-medium">{row.agency_name}</TableCell>
                        <TableCell><PolicyBadge value={row.policy} /></TableCell>
                        <TableCell>
                          <span className="text-xs uppercase tracking-wider text-slate-500">
                            {row.source === 'agency' ? 'Agency' : 'Platform default'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{row.updated_by || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{row.updated_at ? formatDate(row.updated_at) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Audit History */}
        <section data-testid="governance-audit-history">
          <div className="mb-3 flex items-center gap-2">
            <Clock size={16} className="text-slate-500" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Audit History
            </h2>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-400">Loading audit history…</div>
              ) : auditRows.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500" data-testid="governance-audit-empty">
                  No policy activity recorded yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Action</TableHead>
                      <TableHead>Agency</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>When</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditRows.map((row, idx) => (
                      <TableRow key={row.audit_id || row.id || idx}>
                        <TableCell className="text-sm font-mono">{row.action}</TableCell>
                        <TableCell className="text-sm text-slate-600">{row.entity_id}</TableCell>
                        <TableCell className="text-sm text-slate-600">{row.performed_by}</TableCell>
                        <TableCell className="text-sm text-slate-600">{formatDate(row.created_at)}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-md truncate" title={row.details}>
                          {row.details || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="governance-reject-dialog">
          <DialogHeader>
            <DialogTitle>
              {rejectTarget?.type === 'agent_communication_exception'
                ? 'Reject Agent Exception Request'
                : 'Reject Policy Request'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-600">
              Rejecting{' '}
              <span className="font-semibold">
                {rejectTarget?.type === 'agent_communication_exception'
                  ? rejectTarget?.agent_name
                  : rejectTarget?.agency_name}
              </span>&apos;s request to change to <PolicyBadge value={rejectTarget?.requested_policy} />.
              Please provide a reason for the audit trail.
            </div>
            <div className="space-y-1">
              <Label>Rejection reason (required)</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Explain to the requester why this change is not being approved right now."
                data-testid="governance-reject-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={submitting || !rejectReason.trim()}
              data-testid="governance-reject-confirm-btn"
            >
              {submitting ? 'Rejecting…' : 'Reject Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
